import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Alert, AlertDescription } from '../ui/alert';
import { Eye, EyeOff, Mail, Lock, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

export function LoginForm() {
  const { signIn } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [emailFocused, setEmailFocused] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [validationState, setValidationState] = useState({ email: false, password: false });

  // Real-time validation
  useEffect(() => {
    setValidationState({
      email: email.includes('@') && email.includes('.'),
      password: password.length >= 8,
    });
  }, [email, password]);

  const isFormValid = validationState.email && validationState.password;

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      await signIn(email, password);
    } catch (err: unknown) {
      console.error('Login error details:', err);
      
      // Provide more helpful error messages
      const errorMessage = err instanceof Error ? err.message : String(err);
      
      if (errorMessage.includes('Failed to fetch') || errorMessage.includes('fetch')) {
        setError('Cannot connect to server. The backend service may be down. Please try again later.');
      } else if (errorMessage.includes('pending administrator verification') || errorMessage.includes('ACCOUNT_NOT_VERIFIED')) {
        setError('Your account is pending administrator verification. You will receive an email notification at your registered address once your account has been approved.');
      } else if (errorMessage.includes('Invalid login credentials')) {
        setError('Invalid email or password. Please check your credentials and try again.');
      } else if (errorMessage.includes('Email not confirmed')) {
        setError('Please confirm your email address before logging in.');
      } else if (errorMessage.includes('403') || errorMessage.includes('Forbidden')) {
        setError('Access denied. Your account may not be verified yet. Please contact an administrator.');
      } else {
        setError(errorMessage || 'Failed to login. Please check your credentials.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-5">
      <form onSubmit={handleLogin} className="space-y-3 sm:space-y-4">
        <div className="space-y-1.5">
          <Label htmlFor="email" className="sr-only">
            Email address
          </Label>
          <div className={`relative rounded-xl transition-all duration-200 ${
            emailFocused ? 'ring-4 ring-[#1877F2]/15' : ''
          }`}>
            <Mail className={`absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 transition-colors duration-200 ${
              emailFocused ? 'text-[#1877F2]' : 'text-slate-400'
            }`} />
            <Input
              id="email"
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={() => setEmailFocused(true)}
              onBlur={() => setEmailFocused(false)}
              required
              autoComplete="email"
              className="h-11 rounded-xl border-slate-200 bg-white pl-11 pr-11 text-[15px] shadow-sm transition-colors focus:border-[#1877F2] sm:h-12"
            />
            {validationState.email && email && (
              <CheckCircle2 className="absolute right-4 top-1/2 h-4 w-4 -translate-y-1/2 text-emerald-500" />
            )}
          </div>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="password" className="sr-only">
            Password
          </Label>
          <div className={`relative rounded-xl transition-all duration-200 ${
            passwordFocused ? 'ring-4 ring-[#1877F2]/15' : ''
          }`}>
            <Lock className={`absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 transition-colors duration-200 ${
              passwordFocused ? 'text-[#1877F2]' : 'text-slate-400'
            }`} />
            <Input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => setPasswordFocused(true)}
              onBlur={() => setPasswordFocused(false)}
              required
              autoComplete="current-password"
              className="h-11 rounded-xl border-slate-200 bg-white pl-11 pr-11 text-[15px] shadow-sm transition-colors focus:border-[#1877F2] sm:h-12"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className={`absolute inset-y-0 right-4 flex items-center transition-colors duration-200 ${
                passwordFocused ? 'text-[#1877F2]' : 'text-slate-400 hover:text-slate-600'
              }`}
              tabIndex={-1}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          </div>

        </div>

        {error && (
          <Alert variant="destructive" className="border-red-200 bg-red-50 text-xs sm:text-sm">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <AlertDescription className="text-red-800">{error}</AlertDescription>
          </Alert>
        )}

        <Button
          type="submit"
          className="h-11 w-full rounded-xl bg-[#1877F2] text-[15px] font-semibold text-white shadow-[0_14px_28px_rgba(24,119,242,0.28)] transition-all duration-200 hover:bg-[#166fe5] disabled:cursor-not-allowed disabled:opacity-50 sm:h-12"
          disabled={!isFormValid || isLoading}
        >
          {isLoading ? 'Signing in...' : 'Log In'}
        </Button>
      </form>
    </div>
  );
}
